// RadarPlot.js
import { useCallback, useEffect, useMemo, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const RadarPlot = ({
  isLoading,
  data, // EXPECTED: data keyed by node uuid -> { [uuid]: profile }
  timebucketIdx, // index in the *proxy* bucket list
  activeDomains,
  activeEntities,
  activeProxies,
}) => {
  const [chartData, setChartData] = useState(null);
  const [chartLayout, setChartLayout] = useState(null);
  const [showPlot, setShowPlot] = useState(false);

  const proxyIdSet = useMemo(() => {
    return new Set((activeProxies ?? []).map((p) => p.value));
  }, [activeProxies]);

  const createChartLayout = useCallback(() => {
    const plotLayout = {
      polar: {
        bgcolor: "rgba(0,0,0,0)",
        radialaxis: {
          visible: true,
          range: [-40, 100],
          tickvals: [0, 20, 40, 60, 80, 100],
          color: "white",
          width: 1,
          tickfont: { color: "white" },
          gridcolor: "gray",
          linecolor: "gray",
        },
        angularaxis: {
          color: "grey",
          gridcolor: "white",
          tickfont: { color: "white" },
        },
      },
      title: { text: "", font: { color: "white" } },
      width: 1200,
      height: 750,
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      font: { color: "white", size: 16 },
      dragmode: false,
    };

    setChartLayout(plotLayout);
  }, []);

  const createChartData = useCallback(() => {
    // --- Debug: inputs
    console.group("[RadarPlot] createChartData");
    console.log("isLoading:", isLoading);
    console.log("timebucketIdx:", timebucketIdx);
    console.log("activeDomains:", activeDomains);
    console.log("activeEntities:", activeEntities);
    console.log("activeProxies:", activeProxies);
    console.log("data keys:", Object.keys(data || {}));
    console.groupEnd();

    if (!data) return;

    const nodes = [...(activeEntities ?? []), ...(activeProxies ?? [])];

    // Guard: avoid using an invalid proxy bucket index
    const safeProxyBucketIdx =
      typeof timebucketIdx === "number" ? timebucketIdx : -1;

    const plotData = nodes
      .map((node, idx) => {
        const nodeId = node?.value; // uuid
        const nodeName = node?.label; // display name
        const isProxy = proxyIdSet.has(nodeId);

        if (!nodeId || !nodeName) {
          console.warn("SKIP: missing nodeId/nodeName", { node });
          console.groupEnd();
          return null;
        }

        const profile = data?.[nodeId];
        if (!profile) {
          console.warn("SKIP: no profile in `data` for nodeId", {
            nodeId,
            nodeName,
            availableKeys: Object.keys(data || {}),
          });
          console.groupEnd();
          return null;
        }

        // For proxies we use the selected bucket index; for entities use 0
        const bucketIdx = isProxy ? safeProxyBucketIdx : 0;

        if (isProxy && bucketIdx < 0) {
          console.warn("SKIP: proxy has invalid bucketIdx (<0)", {
            nodeId,
            nodeName,
            bucketIdx,
            safeProxyBucketIdx,
            timebucketIdx,
          });
          console.groupEnd();
          return null;
        }

        if (!Array.isArray(activeDomains) || activeDomains.length === 0) {
          console.warn("SKIP: no domains selected");
          console.groupEnd();
          return null;
        }

        // Debug: show which domain keys exist on this profile
        console.log("profile domain keys count:", Object.keys(profile).length);
        console.log("bucketIdx used:", bucketIdx);

        // Interval strings: "val [lb, ub]"
        const entityIntervals = activeDomains.map((domain, dIdx) => {
          const dKey = domain?.value;
          const series = profile?.[dKey];

          const vArr = series?.value;
          const lbArr = series?.value_lb;
          const ubArr = series?.value_ub;

          const v =
            Array.isArray(vArr) && bucketIdx >= 0 ? vArr[bucketIdx] : undefined;
          const lb =
            Array.isArray(lbArr) && bucketIdx >= 0
              ? lbArr[bucketIdx]
              : undefined;
          const ub =
            Array.isArray(ubArr) && bucketIdx >= 0
              ? ubArr[bucketIdx]
              : undefined;

          // Per-domain debug (especially useful for proxies)
          if (isProxy && dIdx === 0) {
            console.log("[Proxy domain 0 debug]", {
              dKey,
              hasSeries: !!series,
              vLen: Array.isArray(vArr) ? vArr.length : null,
              lbLen: Array.isArray(lbArr) ? lbArr.length : null,
              ubLen: Array.isArray(ubArr) ? ubArr.length : null,
              bucketIdx,
              v,
              lb,
              ub,
            });
          }

          // If any part missing -> keep it but show as NaN so Plotly can skip
          return `${v ?? "NaN"} [${lb ?? "NaN"}, ${ub ?? "NaN"}]`;
        });

        if (entityIntervals.length === 0) {
          console.warn("SKIP: entityIntervals empty");
          console.groupEnd();
          return null;
        }

        // Close the radar by repeating first datapoint
        entityIntervals.push(entityIntervals[0]);

        const thetas =
          activeDomains?.length > 0
            ? [...activeDomains.map((d) => d.label), activeDomains[0].label]
            : [];

        const rLower = entityIntervals.map((interval) => {
          // interval: "v [lb, ub]"
          const lbStr = interval.split("[")[1]?.split(", ")[0];
          return Number.parseFloat(lbStr);
        });

        const rUpper = entityIntervals.map((interval) => {
          const ubStr = interval.split(" [")[1]?.split(", ")[1]?.slice(0, -1);
          return Number.parseFloat(ubStr);
        });

        const rValue = entityIntervals.map((interval) => {
          const vStr = interval.split(" [")[0];
          return Number.parseFloat(vStr);
        });

        // Debug: do we have at least SOME numeric values?
        const hasAnyNumeric = rValue.some((x) => Number.isFinite(x));
        if (!hasAnyNumeric) {
          console.warn("SKIP: all rValue are NaN -> nothing to plot", {
            nodeId,
            nodeName,
            isProxy,
            bucketIdx,
          });
          console.groupEnd();
          return null;
        }

        const fillColor = isProxy
          ? "rgba(255,255,255,0.4)"
          : colors[idx % colors.length]
              .replace("rgb", "rgba")
              .replace(")", ",0.4)");

        const lineColor = isProxy
          ? "rgba(255,255,255,1)"
          : colors[idx % colors.length];

        console.log("RENDER: node trace will be created", {
          nodeId,
          nodeName,
          isProxy,
          bucketIdx,
        });
        console.groupEnd();

        return [
          {
            type: "scatterpolar",
            r: rLower,
            theta: thetas,
            mode: "lines",
            line: { color: "transparent" },
            name: "Lower Bound",
            showlegend: false,
            hoverinfo: "none",
          },
          {
            type: "scatterpolar",
            r: rUpper,
            theta: thetas,
            mode: "lines",
            fill: "tonext",
            fillcolor: fillColor,
            line: { color: "transparent" },
            name: "Upper Bound",
            showlegend: false,
            hoverinfo: "none",
          },
          {
            type: "scatterpolar",
            r: rValue,
            theta: thetas,
            name: nodeName,
            line: { color: lineColor },
            customdata: entityIntervals,
            hovertemplate:
              "<b>%{data.name}</b><br>%{theta}: %{customdata}<extra></extra>",
          },
        ];
      })
      .filter(Boolean)
      .flat();

    console.groupCollapsed("[RadarPlot] final plotData");
    console.log("trace count:", plotData.length);
    console.log("plotData:", plotData);
    console.groupEnd();

    setChartData(plotData);
  }, [
    activeDomains,
    activeEntities,
    activeProxies,
    data,
    isLoading,
    proxyIdSet,
    timebucketIdx,
  ]);

  useEffect(() => {
    const hasNodes =
      (activeProxies?.length ?? 0) > 0 || (activeEntities?.length ?? 0) > 0;

    if (
      isLoading ||
      !data ||
      !hasNodes ||
      (activeDomains?.length ?? 0) < 3
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeEntities,
    activeProxies,
    data,
    createChartData,
    createChartLayout,
    isLoading,
    timebucketIdx,
  ]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartLayout && chartData && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
      {!isLoading && showPlot && (!chartData || chartData.length === 0) && (
        <p className="text-sm opacity-70">
          No chart data to display (see console logs).
        </p>
      )}
    </div>
  );
};

export default RadarPlot;